import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, Database, FileSpreadsheet, ShieldAlert, Calendar, History, Link as LinkIcon, Download, RefreshCw } from "lucide-react";

const BAND_COLOR = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  alert: "bg-rose-600",
  no_data: "bg-slate-300",
};

export default function AdminCSA() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  });
  const [filterMonth, setFilterMonth] = useState("");
  const [fetchUrl, setFetchUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = filterMonth ? `/csa/admin/overview?month=${filterMonth}` : "/csa/admin/overview";
      const { data } = await api.get(url);
      setOverview(data);
    } finally { setLoading(false); }
  };

  const loadImports = async () => {
    try {
      const { data } = await api.get("/csa/imports");
      setImports(data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { load(); loadImports(); }, [filterMonth]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a file first"); return; }
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Snapshot month must be YYYY-MM");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("snapshot_month", month);
      const { data } = await api.post("/csa/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,  // 10 minutes for large files
      });
      toast.success(
        `Uploaded — ${data.rows_parsed} rows · ${data.carriers_affected} carriers`
      );
      fileRef.current.value = "";
      await load();
      await loadImports();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(msg.length > 200 ? "Upload failed — file too large for browser. Use 'Fetch from URL' below." : msg);
    } finally { setUploading(false); }
  };

  const handleFetchUrl = async (e) => {
    e.preventDefault();
    if (!fetchUrl.trim()) { toast.error("Paste the FMCSA DataHub URL first"); return; }
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error("Snapshot month must be YYYY-MM");
      return;
    }
    setFetching(true);
    setActiveJob(null);
    try {
      const { data: job } = await api.post("/csa/fetch-url", {
        url: fetchUrl.trim(),
        snapshot_month: month,
      });
      toast.success("Job started — downloading & parsing in background. Don't close this tab.");
      setActiveJob(job);
      pollJob(job.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start fetch job.");
      setFetching(false);
    }
  };

  const pollJob = async (jobId) => {
    const start = Date.now();
    const MAX_MS = 30 * 60 * 1000; // 30 minutes
    while (Date.now() - start < MAX_MS) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data: job } = await api.get(`/csa/jobs/${jobId}`);
        setActiveJob(job);
        if (job.status === "complete") {
          toast.success(
            `Ingested — ${job.result?.rows_parsed || 0} rows · ${job.result?.carriers_affected || 0} carriers`
          );
          setFetching(false);
          setFetchUrl("");
          await load();
          await loadImports();
          return;
        }
        if (job.status === "failed") {
          toast.error(job.error || "Fetch job failed.");
          setFetching(false);
          return;
        }
      } catch (e) {
        // Network blip — keep polling
      }
    }
    setFetching(false);
    toast.error("Job timed out after 30 minutes. Check the backend logs.");
  };

  return (
    <div className="space-y-8" data-testid="admin-csa-page">
      <div>
        <div className="hs-overline text-blue-700">FMCSA · CSA SCORES</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">CSA Snapshots</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          Cross-client view of every carrier&rsquo;s 7 BASIC scores from the official FMCSA SMS monthly dataset.
          {isSuperAdmin && (<> Upload the latest file once a month — every client&rsquo;s dashboard refreshes automatically.</>)}
        </p>
      </div>

      {isSuperAdmin && (
        <div className="hs-card p-6" data-testid="csa-refresh-all-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <RefreshCw size={16} className="text-blue-700" />
                <h3 className="font-display font-bold text-xl">Monthly Refresh — Every Client</h3>
                <span className="hs-tag hs-tag-success">RECOMMENDED MONTHLY</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Triggers a per-client CSA snapshot fetch from <code className="font-mono text-[10px]">data.transportation.gov</code>
                {" "}for every client with a DOT on file. One background job per client — completes in seconds.
                Run this once a month after FMCSA publishes the new dataset.
              </p>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("Trigger CSA refresh for every client with a DOT?")) return;
                try {
                  const { data } = await api.post("/admin/csa/refresh-all-clients");
                  toast.success(`Queued ${data.clients_queued} clients for refresh.`);
                  setTimeout(() => { load(); loadImports(); }, 5000);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Refresh failed");
                }
              }}
              className="hs-btn-primary inline-flex items-center gap-2"
              data-testid="csa-refresh-all-btn"
            >
              <RefreshCw size={14} /> REFRESH ALL CLIENTS NOW
            </button>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="hs-card p-6" data-testid="csa-upload-card">
          <div className="flex items-center gap-2 mb-3">
            <UploadCloud size={16} className="text-blue-700" />
            <h3 className="font-display font-bold text-xl">Upload Monthly FMCSA SMS File</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            Download the monthly snapshot from{" "}
            <a
              href="https://ai.fmcsa.dot.gov/SMS/Tools/Downloads.aspx"
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline font-mono"
            >FMCSA SMS Downloads</a>
            {" "}(free Login.gov account required) — accepts <code>.csv</code>, <code>.tsv</code>, <code>.txt</code>, or <code>.zip</code>.
          </p>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="hs-overline mb-1 block">SMS Data File</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt,.zip"
                className="hs-input"
                data-testid="csa-upload-file"
              />
            </div>
            <div>
              <label className="hs-overline mb-1 block">Snapshot Month (YYYY-MM)</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="hs-input font-mono"
                data-testid="csa-upload-month"
              />
            </div>
            <div className="md:col-span-3">
              <button
                type="submit"
                disabled={uploading}
                className="hs-btn-primary inline-flex items-center gap-2"
                data-testid="csa-upload-submit"
              >
                <UploadCloud size={14} /> {uploading ? "PARSING…" : "UPLOAD & INGEST"}
              </button>
            </div>
          </form>
        </div>
      )}

      {isSuperAdmin && (
        <div className="hs-card p-6" data-testid="csa-fetch-url-card">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon size={16} className="text-blue-700" />
            <h3 className="font-display font-bold text-xl">Fetch from FMCSA DataHub URL</h3>
            <span className="hs-tag hs-tag-success">RECOMMENDED FOR LARGE FILES</span>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            Paste the <strong>direct CSV download URL</strong> from FMCSA&rsquo;s DataHub page
            (e.g. <code className="font-mono text-[10px]">https://data.transportation.gov/api/views/&hellip;/rows.csv?accessType=DOWNLOAD</code>).
            The <strong>server downloads the file directly</strong> from FMCSA — bypasses your browser&rsquo;s upload limits.
            Smart filter: only rows for your registered clients&rsquo; DOT numbers are ingested (lightning fast even on 700K-row files).
          </p>
          <div className="rounded bg-blue-50 border-l-2 border-blue-700 p-3 text-[11px] text-slate-700 mb-4">
            <strong>How to get the URL:</strong>
            <ol className="list-decimal pl-5 mt-1 space-y-1">
              <li>On the FMCSA SMS page, click <em>SMS AB PassProperty | DOT - Public Data Portal</em>.</li>
              <li>On the DataHub page that opens, click the <strong>Export</strong> button (top-right).</li>
              <li>Choose <strong>CSV</strong>. <em>Right-click</em> the &ldquo;Download&rdquo; button → <strong>Copy Link Address</strong>.</li>
              <li>Paste it here ↓ and hit FETCH &amp; INGEST.</li>
            </ol>
          </div>
          <form onSubmit={handleFetchUrl} className="grid grid-cols-1 gap-3">
            <div>
              <label className="hs-overline mb-1 block">DataHub Direct CSV URL</label>
              <input
                type="url"
                placeholder="https://data.transportation.gov/api/views/.../rows.csv?accessType=DOWNLOAD"
                value={fetchUrl}
                onChange={(e) => setFetchUrl(e.target.value)}
                className="hs-input font-mono text-xs"
                data-testid="csa-fetch-url-input"
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={fetching}
                className="hs-btn-primary inline-flex items-center gap-2"
                data-testid="csa-fetch-url-submit"
              >
                <Download size={14} /> {fetching ? "JOB RUNNING — POLLING…" : "FETCH & INGEST"}
              </button>
              <p className="text-[10px] text-slate-400 mt-2">
                Will use the same Snapshot Month set in the upload card above ({month}).
                Server-side download + parse runs in the background — safe to leave this tab open.
              </p>
              {activeJob && (
                <div className="mt-4 hs-card p-4 border-l-2 border-blue-700" data-testid="csa-job-status">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="hs-overline text-[10px] text-slate-500">JOB STATUS</div>
                      <div className="text-sm font-medium mt-1">
                        <span className={`hs-tag ${
                          activeJob.status === "complete" ? "hs-tag-success" :
                          activeJob.status === "failed" ? "hs-tag-danger" :
                          "hs-tag-warn"
                        }`}>{(activeJob.status || "").toUpperCase()}</span>
                        {activeJob.byte_size && (
                          <span className="ml-3 text-xs text-slate-500 font-mono">
                            {(activeJob.byte_size / 1024 / 1024).toFixed(1)} MB downloaded
                          </span>
                        )}
                      </div>
                    </div>
                    {activeJob.result && (
                      <div className="text-right">
                        <div className="hs-overline text-[10px] text-slate-500">RESULT</div>
                        <div className="font-mono text-sm">
                          {activeJob.result.rows_parsed} rows · {activeJob.result.carriers_affected} carriers
                        </div>
                      </div>
                    )}
                  </div>
                  {activeJob.error && (
                    <div className="mt-3 text-xs text-rose-600 bg-rose-50 border-l-2 border-rose-600 p-2">
                      {activeJob.error}
                    </div>
                  )}
                  {activeJob.status && !["complete", "failed"].includes(activeJob.status) && (
                    <div className="mt-3 h-1 bg-slate-100 overflow-hidden rounded">
                      <div className="h-full bg-blue-700 animate-pulse" style={{ width: "60%" }}></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="hs-card p-6" data-testid="csa-overview-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-blue-700" />
            <h3 className="font-display font-bold text-xl">Carrier CSA Grid</h3>
            {overview?.month_used && (
              <span className="hs-tag hs-tag-info font-mono">{overview.month_used}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={12} className="text-slate-400" />
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="hs-input text-xs font-mono"
              data-testid="csa-month-filter"
            >
              <option value="">Latest available</option>
              {(overview?.months_available || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="hs-overline">LOADING…</div>
        ) : overview?.rows?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] hs-overline text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Carrier</th>
                  <th className="px-2">DOT</th>
                  <th className="px-2">Snapshot</th>
                  <th className="px-2 text-right">Alerts</th>
                  <th className="px-2">BASIC Status</th>
                </tr>
              </thead>
              <tbody>
                {overview.rows.map((r) => (
                  <tr key={r.client_id} className="border-b border-slate-100" data-testid={`csa-row-${r.dot_number}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{r.company_name || "—"}</div>
                      <div className="text-[11px] text-slate-500">{r.email}</div>
                    </td>
                    <td className="px-2 font-mono">{r.dot_number || "—"}</td>
                    <td className="px-2 font-mono text-xs">{r.snapshot_month || "—"}</td>
                    <td className="px-2 text-right">
                      {r.has_data ? (
                        <span className={`hs-tag ${r.alert_count > 0 ? "hs-tag-danger" : "hs-tag-success"}`}>
                          {r.alert_count} / 7
                        </span>
                      ) : (
                        <span className="hs-tag hs-tag-info">NO DATA</span>
                      )}
                    </td>
                    <td className="px-2">
                      <div className="flex gap-1">
                        {["unsafe_driving","hours_of_service","driver_fitness","controlled_substances","vehicle_maintenance","hazmat","crash_indicator"].map((k) => {
                          const b = r.basics?.[k] || {};
                          const cls = BAND_COLOR[b.band || "no_data"];
                          const title = `${k}: ${typeof b.percentile === "number" ? b.percentile.toFixed(1) + "%" : "—"}`;
                          return <span key={k} title={title} className={`inline-block w-3 h-3 rounded-sm ${cls}`}></span>;
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-4">
              Squares left-to-right: Unsafe Driving · HOS · Driver Fitness · Controlled Subst. · Vehicle Maint. · HazMat · Crash Indicator.
              Green = ok · Amber = watch · Red = ALERT (above FMCSA threshold) · Gray = no data.
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <ShieldAlert size={32} className="text-slate-300 mx-auto" />
            <p className="text-sm text-slate-500 mt-3">
              No CSA snapshots loaded yet.
              {isSuperAdmin && " Upload the FMCSA SMS monthly file above to get started."}
            </p>
          </div>
        )}
      </div>

      {imports.length > 0 && (
        <div className="hs-card p-6" data-testid="csa-imports-card">
          <div className="flex items-center gap-2 mb-3">
            <History size={16} className="text-blue-700" />
            <h3 className="font-display font-bold text-xl">Upload History</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] hs-overline text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">File</th>
                <th className="px-2">Month</th>
                <th className="px-2 text-right">Rows</th>
                <th className="px-2 text-right">Carriers</th>
                <th className="px-2">Uploaded</th>
                <th className="px-2">By</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((i) => (
                <tr key={i.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 flex items-center gap-2">
                    <FileSpreadsheet size={12} className="text-slate-400" /> {i.filename}
                  </td>
                  <td className="px-2 font-mono">{i.snapshot_month_fallback}</td>
                  <td className="px-2 text-right font-mono">{i.rows_parsed}</td>
                  <td className="px-2 text-right font-mono">{i.carriers_affected}</td>
                  <td className="px-2 font-mono text-xs">{(i.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-2 text-xs">{i.uploaded_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
